import { useEffect, useState } from "react";
import axios from "axios";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fyStartYear = (d = new Date()) => (d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1);
const fyLabel = (y) => `FY ${y}-${String((y + 1) % 100).padStart(2, "0")}`;

export default function Reports() {
  const [year, setYear] = useState(fyStartYear());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [year]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/gst-summary?year=${year}`);
      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };

  const fmtCurrency = (val) =>
    `\u20b9${(val || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="p-4 sm:p-8" data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">
          GST Summary Report
        </h1>
        <p className="text-slate-500 text-sm">
          Monthly GST breakdown for tax filing
        </p>
      </div>

      {/* Year Selector */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => setYear(year - 1)}
          data-testid="prev-year-btn"
          className="p-2 bg-white border border-slate-200 rounded-sm hover:bg-slate-50 transition-all duration-200"
        >
          <CaretLeft size={20} weight="bold" />
        </button>
        <span
          data-testid="current-year"
          className="text-2xl font-black tracking-tight text-slate-900 min-w-[80px] text-center"
        >
          {fyLabel(year)}
        </span>
        <button
          onClick={() => setYear(year + 1)}
          data-testid="next-year-btn"
          className="p-2 bg-white border border-slate-200 rounded-sm hover:bg-slate-50 transition-all duration-200"
        >
          <CaretRight size={20} weight="bold" />
        </button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 bg-slate-200 rounded"></div>
          ))}
        </div>
      ) : !reportData || reportData.months.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/c2875c4c-3209-4e71-8b15-de610df9cec1/images/6f94e3573c58a84c73fc78db7a1660d6d91c6b0e4489f8721400bcc17382d354.png"
            alt="No data"
            className="w-48 h-48 mx-auto mb-6 opacity-60"
          />
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            No data for {fyLabel(year)}
          </h3>
          <p className="text-slate-500">
            No invoices found for the selected financial year (Apr–Mar)
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Total Taxable Value
              </p>
              <p
                data-testid="total-taxable-value"
                className="text-2xl font-black tracking-tight text-slate-900"
              >
                {fmtCurrency(reportData.totals.taxable_value)}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Total GST Collected
              </p>
              <p
                data-testid="total-gst-collected"
                className="text-2xl font-black tracking-tight text-blue-600"
              >
                {fmtCurrency(reportData.totals.total_gst)}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Total Invoices
              </p>
              <p
                data-testid="total-invoices-report"
                className="text-2xl font-black tracking-tight text-slate-900"
              >
                {reportData.totals.total_invoices}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Total Amount
              </p>
              <p
                data-testid="total-amount-report"
                className="text-2xl font-black tracking-tight text-slate-900"
              >
                {fmtCurrency(reportData.totals.total_amount)}
              </p>
            </div>
          </div>

          {/* Monthly Breakdown Table */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                Monthly Breakdown
              </h2>
            </div>
            <div className="overflow-x-auto sticky-table">
              <table className="w-full">
                <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                      Month
                    </th>
                    <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                      Invoices
                    </th>
                    <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                      Taxable Value
                    </th>
                    <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                      CGST
                    </th>
                    <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                      SGST
                    </th>
                    <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                      IGST
                    </th>
                    <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                      Total GST
                    </th>
                    <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {reportData.months.map((month) => (
                    <tr
                      key={month.month}
                      data-testid={`report-row-${month.month}`}
                      className="hover:bg-slate-50 transition-colors duration-150"
                    >
                      <td className="px-5 py-4 text-sm font-semibold text-slate-900">
                        {month.month_name}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600 text-right">
                        {month.total_invoices}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600 text-right">
                        {fmtCurrency(month.taxable_value)}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600 text-right">
                        {fmtCurrency(month.cgst)}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600 text-right">
                        {fmtCurrency(month.sgst)}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600 text-right">
                        {fmtCurrency(month.igst)}
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-blue-600 text-right">
                        {fmtCurrency(month.total_gst)}
                      </td>
                      <td className="px-5 py-4 text-sm font-bold text-slate-900 text-right">
                        {fmtCurrency(month.total_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-900">
                  <tr className="font-bold">
                    <td className="px-5 py-4 text-sm text-slate-900">Total</td>
                    <td className="px-5 py-4 text-sm text-slate-900 text-right">
                      {reportData.totals.total_invoices}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-900 text-right">
                      {fmtCurrency(reportData.totals.taxable_value)}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-900 text-right">
                      {fmtCurrency(reportData.totals.cgst)}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-900 text-right">
                      {fmtCurrency(reportData.totals.sgst)}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-900 text-right">
                      {fmtCurrency(reportData.totals.igst)}
                    </td>
                    <td className="px-5 py-4 text-sm text-blue-700 text-right">
                      {fmtCurrency(reportData.totals.total_gst)}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-900 text-right">
                      {fmtCurrency(reportData.totals.total_amount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* GST Split Summary */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Total CGST
              </p>
              <p
                data-testid="annual-cgst"
                className="text-xl font-black text-slate-900"
              >
                {fmtCurrency(reportData.totals.cgst)}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Total SGST
              </p>
              <p
                data-testid="annual-sgst"
                className="text-xl font-black text-slate-900"
              >
                {fmtCurrency(reportData.totals.sgst)}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Total IGST
              </p>
              <p
                data-testid="annual-igst"
                className="text-xl font-black text-slate-900"
              >
                {fmtCurrency(reportData.totals.igst)}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
